import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Search, MapPin, Calendar, DollarSign, Briefcase, Camera, Video, Filter, X, ChevronRight, Lock, Sparkles, Loader2, Tag } from "lucide-react";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function BrowseJobs() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showFilters, setShowFilters] = React.useState(false);
  const [searchAddress, setSearchAddress] = React.useState("");
  const [coordinates, setCoordinates] = React.useState(null);
  const [radius, setRadius] = React.useState(25);
  const [jobType, setJobType] = React.useState("all");
  const [subscription, setSubscription] = React.useState(null);
  const [checkingSubscription, setCheckingSubscription] = React.useState(true);
  const [subscribing, setSubscribing] = React.useState(false);
  const [discountCode, setDiscountCode] = React.useState("");
  const [clientSecret, setClientSecret] = React.useState(null);
  const [validatedDiscount, setValidatedDiscount] = React.useState(null);
  const [validatingCode, setValidatingCode] = React.useState(false);
  const [userLocationLoaded, setUserLocationLoaded] = React.useState(false);
  const [usingHomeLocation, setUsingHomeLocation] = React.useState(false);

  async function validateDiscountCode() {
    if (!discountCode.trim()) return;
    setValidatingCode(true);
    try {
      var response = await fetch(API + "/admin/discount-codes/validate?code=" + encodeURIComponent(discountCode), {
        method: "POST",
        headers: { Authorization: "Bearer " + token }
      });
      var data = await response.json();
      if (response.ok && data.valid) {
        setValidatedDiscount(data);
        toast.success("Discount code applied!");
      } else {
        toast.error(data.error || "Invalid discount code");
        setValidatedDiscount(null);
      }
    } catch(e) {
      toast.error("Failed to validate code");
    } finally {
      setValidatingCode(false);
    }
  }

  // Check subscription status first
  React.useEffect(function() {
    checkSubscription();
  }, [token]);

  // Resolve user's home location as default coordinates
  React.useEffect(function() {
    if (checkingSubscription || !user) {
      if (!checkingSubscription && !user) setUserLocationLoaded(true);
      return;
    }
    resolveUserLocation();
  }, [checkingSubscription, user]);

  // Fetch jobs once location is resolved (or skipped)
  React.useEffect(function() {
    if (!checkingSubscription && userLocationLoaded) {
      fetchJobs();
    }
  }, [coordinates, radius, jobType, checkingSubscription, userLocationLoaded]);

  async function resolveUserLocation() {
    // Try GeoJSON coordinates first (user.location.coordinates = [lng, lat])
    var loc = user.location;
    if (loc && loc.coordinates && loc.coordinates.length >= 2) {
      setCoordinates({ lat: loc.coordinates[1], lng: loc.coordinates[0] });
      setRadius(loc.work_radius || 25);
      setUsingHomeLocation(true);
      setUserLocationLoaded(true);
      return;
    }

    // Fallback: geocode city/state from location_info or location
    var info = user.location_info || loc || {};
    var city = info.city;
    var state = info.state;
    if (city && token) {
      try {
        var query = city + (state ? ", " + state : "");
        var response = await fetch(API + "/geocode?address=" + encodeURIComponent(query), {
          headers: { Authorization: "Bearer " + token }
        });
        if (response.ok) {
          var data = await response.json();
          setCoordinates({ lat: data.lat, lng: data.lng });
          setUsingHomeLocation(true);
        }
      } catch (e) {
        console.error("Failed to geocode home location:", e);
      }
    }
    setUserLocationLoaded(true);
  }

  async function checkSubscription() {
    if (!token) {
      setCheckingSubscription(false);
      return;
    }
    
    try {
      const response = await fetch(API + "/subscriptions/status", {
        headers: { Authorization: "Bearer " + token }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error("Subscription check error:", error);
    } finally {
      setCheckingSubscription(false);
    }
  }

  const hasSubscription = subscription && subscription.can_view_jobs;
  const hasLocation = user && ((user.location && user.location.city) || (user.location_info && user.location_info.city));

  async function fetchJobs() {
    try {
      var params = new URLSearchParams();
      params.append("status", "open");
      
      if (coordinates) {
        params.append("lat", coordinates.lat);
        params.append("lng", coordinates.lng);
        params.append("radius", radius);
      }
      
      if (jobType && jobType !== "all") {
        params.append("job_type", jobType);
      }

      // Non-subscribers see teaser cards
      if (!hasSubscription) {
        params.append("teaser", "true");
      }

      var headers = token ? { Authorization: "Bearer " + token } : {};
      var response = await fetch(API + "/jobs?" + params.toString(), {
        headers: headers
      });
      
      if (response.ok) {
        var data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error("Fetch jobs error:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }

  async function handleAddressSearch() {
    if (!searchAddress.trim()) {
      // Reset to home location
      setUsingHomeLocation(false);
      setCoordinates(null);
      setUserLocationLoaded(false);
      resolveUserLocation();
      return;
    }

    try {
      var response = await fetch(API + "/geocode?address=" + encodeURIComponent(searchAddress), {
        headers: {
          Authorization: "Bearer " + token
        }
      });
      if (response.ok) {
        var data = await response.json();
        setCoordinates({ lat: data.lat, lng: data.lng });
        setUsingHomeLocation(false);
        toast.success("Searching near " + data.formatted_address);
      } else {
        toast.error("Could not find that address");
      }
    } catch (error) {
      console.error("Geocode error:", error);
      toast.error("Location search failed");
    }
  }

  function clearFilters() {
    setSearchAddress("");
    setJobType("all");
    setRadius(25);
    // Reset back to user's home location instead of clearing completely
    setUsingHomeLocation(false);
    setCoordinates(null);
    setUserLocationLoaded(false);
    // Re-trigger home location resolution
    resolveUserLocation();
  }

  async function handleSubscribe(planType) {
    if (!user) {
      navigate(`/register?plan=${planType}`);
      return;
    }

    setSubscribing(true);
    try {
      const params = new URLSearchParams({
        plan_type: planType,
        origin_url: window.location.origin,
        embedded: "true"
      });
      if (discountCode.trim()) {
        params.append("discount_code", validatedDiscount?.code || discountCode.trim());
      }

      const response = await fetch(API + "/subscriptions/checkout?" + params.toString(), {
        method: "POST",
        headers: { Authorization: "Bearer " + token }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.client_secret) {
          setClientSecret(data.client_secret);
        } else if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    } finally {
      setSubscribing(false);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return "TBD";
    var datePart = dateStr.split("T")[0];
    var parts = datePart.split("-");
    var date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function formatTime(timeStr) {
    if (!timeStr) return "";
    var parts = timeStr.split(":");
    var hr = parseInt(parts[0]);
    var min = parts[1];
    var ampm = hr >= 12 ? "PM" : "AM";
    var h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return h12 + ":" + min + " " + ampm;
  }

  function getJobTypeIcon(type) {
    if (type === "photo") return React.createElement(Camera, { className: "w-4 h-4" });
    if (type === "video") return React.createElement(Video, { className: "w-4 h-4" });
    return React.createElement(Briefcase, { className: "w-4 h-4" });
  }

  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="animate-pulse text-white/50">Loading...</div>
      </div>
    );
  }

  // Show embedded checkout if client_secret is available
  if (clientSecret) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <NavBar>
          <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
            Dashboard
          </Link>
          <NavActions />
        </NavBar>

        <main className="pt-24 pb-12 px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">Complete Your Subscription</h1>
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={function() { setClientSecret(null); }} data-testid="back-to-plans-btn">
                Back to Plans
              </Button>
            </div>
            <div id="checkout" className="bg-white rounded-lg overflow-hidden" data-testid="embedded-checkout">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Dashboard
        </Link>
        {user && (
          <Link to={"/profile/" + user.user_id} className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
            Profile
          </Link>
        )}
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Jobs Near You
            </h1>
            <p className="text-white/50">Find photography and videography opportunities in your area</p>
          </div>

          {/* Location missing banner */}
          {user && !hasLocation && (
            <Card className="bg-yellow-500/10 border-yellow-500/30 p-5 mb-6" data-testid="no-location-banner">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-300 mb-1">Add your location to see jobs near you</p>
                  <p className="text-white/50 text-sm">We need your city and state to show you relevant jobs in your area. You can search by location below, or add your location to your profile for automatic results.</p>
                  <Link to="/profile/edit" className="inline-flex items-center gap-1.5 mt-2 text-sm text-yellow-400 hover:text-yellow-300 font-medium">
                    Update my profile
                  </Link>
                </div>
              </div>
            </Card>
          )}

          {/* Free plan info banner */}
          {user && !hasSubscription && (
            <Card className="bg-white/[0.03] border-white/10 p-5 mb-6" data-testid="free-plan-banner">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-white/80 text-sm">
                    <strong>You are on the free plan.</strong> Studios can still discover your profile and reach out to you directly if they think you are a good fit.
                    Subscribe to see full job details and apply to open positions.
                  </p>
                </div>
                <Link to="/pricing">
                  <Button className="bg-coral hover:bg-coral/90 text-white font-bold rounded-sm shrink-0" data-testid="upgrade-btn">
                    View Plans
                  </Button>
                </Link>
              </div>
            </Card>
          )}

          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="text"
                    value={searchAddress}
                    onChange={function(e) { setSearchAddress(e.target.value); }}
                    onKeyPress={function(e) { if (e.key === "Enter") handleAddressSearch(); }}
                    placeholder="Enter city or zip code..."
                    className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                    data-testid="location-search-input"
                  />
                </div>
                <Button onClick={handleAddressSearch} className="bg-white text-black hover:bg-gray-200 rounded-sm h-12 px-6" data-testid="location-search-btn">
                  <Search className="w-5 h-5" />
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={function() { setShowFilters(!showFilters); }}
                className="border-white/20 text-white hover:bg-white/10 rounded-sm h-12"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>

            {showFilters && (
              <Card className="bg-white/[0.03] border-white/5 p-6 mb-4" data-testid="filters-panel">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="text-sm text-white/60 uppercase tracking-wider block mb-3">
                      Search Radius: {radius} miles
                    </label>
                    <Slider
                      value={[radius]}
                      onValueChange={function(val) { setRadius(val[0]); }}
                      min={5}
                      max={100}
                      step={5}
                      className="w-full"
                      data-testid="radius-slider"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-white/60 uppercase tracking-wider block mb-3">
                      Job Type
                    </label>
                    <select 
                      value={jobType} 
                      onChange={function(e) { setJobType(e.target.value); }}
                      className="w-full h-10 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
                      data-testid="job-type-select"
                    >
                      <option value="all">All Types</option>
                      <option value="photo">Photography</option>
                      <option value="video">Videography</option>
                      <option value="content">Content Creation</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <Button variant="ghost" onClick={clearFilters} className="text-white/60 hover:text-white" data-testid="clear-filters-btn">
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {(coordinates || jobType !== "all") && (
              <div className="flex flex-wrap gap-2 mb-2">
                {coordinates && (
                  <Badge className="bg-white/10 text-white border-white/20 rounded-sm px-3 py-1" data-testid="location-badge">
                    <MapPin className="w-3 h-3 mr-1 inline" />
                    {usingHomeLocation ? "Your area" : "Search"} • {radius} miles
                  </Badge>
                )}
                {jobType !== "all" && (
                  <Badge className="bg-white/10 text-white border-white/20 rounded-sm px-3 py-1">
                    {jobType === "photo" ? "Photography" : jobType === "video" ? "Videography" : "Content"}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-white/50">Loading jobs...</div>
            </div>
          ) : jobs.length === 0 ? (
            <Card className="bg-white/[0.03] border-white/5 p-12 text-center" data-testid="no-jobs-found">
              <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                {usingHomeLocation ? "No jobs in your area yet" : "No jobs found"}
              </h3>
              <p className="text-white/50 mb-4">
                {usingHomeLocation
                  ? "There are no open jobs within " + radius + " miles of your location right now. Try expanding your radius or check back soon — new jobs are posted regularly."
                  : "Try adjusting your filters or search in a different area"}
              </p>
              {usingHomeLocation && (
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm"
                  onClick={function() { setRadius(Math.min(radius + 25, 100)); }}
                  data-testid="expand-radius-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Expand to {Math.min(radius + 25, 100)} miles
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              <p className="text-white/50 text-sm">{jobs.length} job{jobs.length !== 1 ? "s" : ""} found {!hasSubscription ? "— subscribe to see full details" : ""}</p>
              {jobs.map(function(job) {
                // Teaser card for non-subscribers
                if (!hasSubscription) {
                  var locCity = (job.location_info || job.location_display || job.location || {}).city || "";
                  var locState = (job.location_info || job.location_display || job.location || {}).state || "";
                  var locStr = locCity ? (locCity + (locState ? ", " + locState : "")) : "Location available after subscribing";
                  return (
                    <Card key={job.job_id} className="bg-white/[0.03] border-white/5 p-6 relative overflow-hidden" data-testid={"job-card-teaser-" + job.job_id}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getJobTypeIcon(job.job_type)}
                            <h3 className="text-xl font-semibold">{job.title || job.role_needed || "Photography Job"}</h3>
                          </div>
                          <div className="flex flex-wrap gap-4 text-white/50 text-sm mt-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(job.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {locStr}
                              {job.distance ? " • " + Math.round(job.distance) + " mi" : ""}
                            </span>
                            {(job.hourly_rate || job.flat_rate) && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {job.hourly_rate ? "$" + job.hourly_rate + "/hr" : "$" + job.flat_rate + " flat"}
                              </span>
                            )}
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <Lock className="w-3.5 h-3.5 text-white/30" />
                            <span className="text-white/30 text-sm italic">Studio name, description, and venue hidden. Subscribe to view full details and apply.</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                }

                // Full card for subscribers
                return (
                  <Link to={"/jobs/" + job.job_id} key={job.job_id}>
                    <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover" data-testid={"job-card-" + job.job_id}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getJobTypeIcon(job.job_type)}
                            <h3 className="text-xl font-semibold">{job.title}</h3>
                          </div>
                          <p className="text-white/60 mb-3 line-clamp-2">{job.description}</p>
                          <div className="flex flex-wrap gap-4 text-white/50 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(job.date)}{job.start_time ? " at " + formatTime(job.start_time) : ""}
                              {job.start_time && job.end_time ? " – " + formatTime(job.end_time) : ""}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.venue_name ? job.venue_name + ", " : ""}
                              {job.location_info?.city
                                ? job.location_info.city + (job.location_info.state ? ", " + job.location_info.state : "")
                                : (job.location_display?.city
                                  ? job.location_display.city + (job.location_display.state ? ", " + job.location_display.state : "")
                                  : (typeof job.location === "object" && job.location?.city
                                    ? job.location.city
                                    : "Location TBD"))}
                              {job.distance ? " • " + job.distance + " mi" : ""}
                            </span>
                            {(job.hourly_rate || job.flat_rate) && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {job.hourly_rate ? "$" + job.hourly_rate + "/hr" : "$" + job.flat_rate + " flat"}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-white/40 text-sm">{job.studio_name}</span>
                          {job.my_match && (
                            <Badge className={
                              job.my_match.tier === "excellent" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                              job.my_match.tier === "good" ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" :
                              "bg-white/10 text-white/60 border-white/20"
                            } data-testid={"match-badge-" + job.job_id}>
                              {job.my_match.score}% Match
                            </Badge>
                          )}
                          <ChevronRight className="w-5 h-5 text-white/30" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default BrowseJobs;
